import { Modal, Button, Alert } from '@/shared/components/ui';
import { AlertTriangle, MonitorSmartphone, Clock } from 'lucide-react';
import { useState } from 'react';
import type { DispositivoInfo } from '@/core/sdk/modules/administracion/sesionesClient';
import styles from './ModalSesionDuplicada.module.css';

interface ModalSesionDuplicadaProps {
    /** Si el modal está abierto */
    open: boolean;
    /** Tipo de sesión duplicada: local (otra tab) o remota (otro dispositivo) */
    tipo: 'local' | 'remota';
    /** Info del dispositivo conflictivo (solo si tipo=remota) */
    dispositivoConflictivo?: DispositivoInfo | null;
    /** Callback para cerrar esta tab/ventana */
    onCerrarEstaTab: () => void;
    /** Callback para cerrar la otra sesión y continuar aquí */
    onCerrarOtraSesion: () => Promise<void>;
}

/**
 * ModalSesionDuplicada - Modal de advertencia de sesión duplicada
 * 
 * Casos:
 * - Local: Usuario abrió la app en otra tab del mismo browser
 * - Remota: Usuario abrió la app en otro dispositivo (PC, tablet, móvil)
 */
export const ModalSesionDuplicada = ({
    open,
    tipo,
    dispositivoConflictivo,
    onCerrarEstaTab,
    onCerrarOtraSesion,
}: ModalSesionDuplicadaProps) => {
    const [cerrandoOtra, setCerrandoOtra] = useState(false);

    const handleCerrarOtra = async () => {
        setCerrandoOtra(true);
        try {
            await onCerrarOtraSesion();
        } catch (error) {
            console.error('Error al cerrar otra sesión:', error);
        } finally {
            setCerrandoOtra(false);
        }
    };

    // Formatear última actividad
    const formatearUltimaActividad = (fecha?: Date | string): string => {
        if (!fecha) return '';

        // Convertir a Date si es string (desde localStorage)
        const fechaObj = typeof fecha === 'string' ? new Date(fecha) : fecha;

        const ahora = new Date();
        const diff = Math.floor((ahora.getTime() - fechaObj.getTime()) / 1000); // segundos

        if (diff < 60) return 'hace menos de 1 minuto';
        if (diff < 120) return 'hace 1 minuto';
        if (diff < 3600) return `hace ${Math.floor(diff / 60)} minutos`;
        if (diff < 7200) return 'hace 1 hora';
        return `hace ${Math.floor(diff / 3600)} horas`;
    };

    return (
        <Modal
            isOpen={open}
            onClose={() => { }} // No cerrable con ESC/backdrop (usuario debe elegir)
            title={tipo === 'local' ? 'Sesión Abierta en Otra Pestaña' : 'Sesión Activa en Otro Dispositivo'}
            size="md"
            closeOnOverlayClick={false}
        >
            <div className={styles.container}>
                {/* Header con icono */}
                <div className={styles.header}>
                    <div className={styles.iconWrapper}>
                        {tipo === 'local' ? (
                            <AlertTriangle className={styles.icon} size={48} />
                        ) : (
                            <MonitorSmartphone className={styles.icon} size={48} />
                        )}
                    </div>
                </div>

                {/* Contenido según tipo */}
                {tipo === 'local' ? (
                    <>
                        <Alert type="warning">
                            La aplicación está abierta en otra pestaña del navegador.
                        </Alert>

                        <p className={styles.description}>
                            Por seguridad, solo puede haber una sesión activa a la vez.
                            <br />
                            Por favor, cierra una de las pestañas para continuar.
                        </p>

                        <div className={styles.actions}>
                            <Button
                                variant="danger"
                                onClick={onCerrarEstaTab}
                                fullWidth
                            >
                                Cerrar Esta Pestaña
                            </Button>
                            <Button
                                variant="primary"
                                onClick={handleCerrarOtra}
                                loading={cerrandoOtra}
                                fullWidth
                            >
                                Usar Aquí y Cerrar Otra
                            </Button>
                        </div>
                    </>
                ) : (
                    <>
                        <Alert type="warning">
                            Detectamos una sesión activa en otro dispositivo.
                        </Alert>

                        {dispositivoConflictivo && (
                            <div className={styles.deviceInfo}>
                                <div className={styles.deviceInfoRow}>
                                    <MonitorSmartphone size={20} />
                                    <div>
                                        <strong>{dispositivoConflictivo.nombre}</strong>
                                        <p>{dispositivoConflictivo.navegador}</p>
                                    </div>
                                </div>
                                <div className={styles.deviceInfoRow}>
                                    <Clock size={16} />
                                    <span className={styles.lastActivity}>
                                        Última actividad: {formatearUltimaActividad(dispositivoConflictivo.ultimaActividad)}
                                    </span>
                                </div>
                            </div>
                        )}

                        <p className={styles.description}>
                            ¿Deseas cerrar esa sesión y continuar aquí?
                        </p>

                        <div className={styles.actions}>
                            <Button
                                variant="danger"
                                onClick={onCerrarEstaTab}
                                fullWidth
                            >
                                Cerrar Esta Sesión
                            </Button>
                            <Button
                                variant="primary"
                                onClick={handleCerrarOtra}
                                loading={cerrandoOtra}
                                fullWidth
                            >
                                Cerrar Otra Sesión y Continuar
                            </Button>
                        </div>
                    </>
                )}
            </div>
        </Modal>
    );
};
